import type { PhotoStep } from '../types';

/** Fields needed to compute step capacity / completion. */
export type PhotoStepLimits = Pick<PhotoStep, 'photoCount'> & {
  maxAdditionalPhotos?: number | null;
};

/** Step limits plus id, for session-wide required/extra checks. */
export type PhotoStepLimitsWithId = PhotoStepLimits & Pick<PhotoStep, 'id'>;

/** Total photos allowed for a step (required + optional extras). */
export function effectiveMaxPhotos(step: PhotoStepLimits): number {
  return step.photoCount + (step.maxAdditionalPhotos ?? 0);
}

/** Whether the step has met its required photoCount (extras ignored). */
export function isStepRequiredDone(step: PhotoStepLimits, count: number): boolean {
  return count >= step.photoCount;
}

/**
 * Whether the step list shows a checkmark.
 * A fully optional step (photoCount 0) stays numbered until it has a photo,
 * so an extra photo does not look already done. Required steps still check
 * off once photoCount is met. Display only — navigation uses isStepRequiredDone.
 */
export function isStepMarkedComplete(step: PhotoStepLimits, count: number): boolean {
  return count >= Math.max(step.photoCount, 1);
}

/** Whether the step can still accept more photos under its total cap. */
export function canAddMorePhotos(step: PhotoStepLimits, count: number): boolean {
  return count < effectiveMaxPhotos(step);
}

/** Uploaded photo count per step id. */
export function countPhotosByStepId(photos: { stepId: string }[] | undefined): Record<string, number> {
  return (photos ?? []).reduce(
    (acc, photo) => {
      acc[photo.stepId] = (acc[photo.stepId] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );
}

/** Whether every step has met its required photoCount (extras ignored). */
export function allRequiredStepsDone(steps: PhotoStepLimitsWithId[], counts: Record<string, number>): boolean {
  return steps.every((step) => isStepRequiredDone(step, counts[step.id] ?? 0));
}

/**
 * Where to go after leaving `currentStepId`: the first step anywhere still
 * missing required photos, else the first later step that can still take
 * photos (extras or a fully optional step), else undefined = complete.
 * Omit `currentStepId` to consider every step (session start).
 */
export function nextStep<T extends PhotoStepLimitsWithId>(
  steps: T[],
  currentStepId: string | undefined,
  counts: Record<string, number>
): T | undefined {
  const required = steps.find((step) => !isStepRequiredDone(step, counts[step.id] ?? 0));
  if (required) return required;
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);
  return steps.slice(currentIndex + 1).find((step) => canAddMorePhotos(step, counts[step.id] ?? 0));
}

/** Whether any step can still accept photos under its total cap. */
export function anyStepCanAddMore(steps: PhotoStepLimitsWithId[], counts: Record<string, number>): boolean {
  return steps.some((step) => canAddMorePhotos(step, counts[step.id] ?? 0));
}
